import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faXmark } from "@fortawesome/free-solid-svg-icons";

const suggestions = [
  "Admission Process",
  "Apply Now",
  "Courses Offered",
  "Contact Details",
  "Life@Campus",
];

const ChatBox = ({ isActive, toggle }) => {
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const knowledgeBase = [
    {
      question: "Admission Process",
      answer:
        "📝 Admission Process:\n1. Apply Online.\n2. Bring Required Documents:\n• Birth Certificate\n• Previous School Records\n• Passport Size Photos (3)\n• Transfer Certificate (if applicable)\n• Aadhar Card Copy",
    },
    {
      question: "Apply Now",
      answer: (
        <span>
          📋 You can apply now using this form:&nbsp;
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSdW5AwmV3DSirB38HJofn3US9UFnEAHIaUji_2Ayd4a8AZ92Q/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline hover:text-blue-700 transition-colors"
          >
            Apply Here
          </a>
        </span>
      ),
    },
    {
      question: "Courses Offered",
      answer:
        "📚 Courses Offered:\n1. Maths / Physics / Chemistry / Biology\n2. Maths / Physics / Chemistry / Computer Science\n3. Commerce / Economics / Accountancy / Computer Application\n4. Commerce / Economics / Accountancy / Business Maths",
    },
    {
      question: "Contact Details",
      answer:
        "📞 Contact Details:\nPhone:\n+91 89396 49600\n+91 90879 17905\nEmail:\ninfo@stvmhss.in \nst0581thomasvid@gmail",
    },
    {
      question: "Life@Campus",
      answer:
        "🏫 Life @ Campus:\nEnjoyable campus with a friendly environment where students thrive academically and socially through various engaging activities.",
    },
  ];

  // Bot response logic
  const getBotResponse = (userInput) => {
    const input = userInput.toLowerCase().trim();

    // Respond to greetings
    const greetings = [
      "Hey",
      "Hello",
      "Hi",
      "hi",
      "hello",
      "hey",
      "Good morning",
      "Good afternoon",
      "Good evening",
    ];
    if (greetings.some((greet) => input.includes(greet))) {
      return "👋 Hello! I am Shasha, your school assistant. You can ask me about admission, courses, contact details, and campus life.";
    }

    // Check school-related questions
    const match = knowledgeBase.find((item) =>
      input.includes(item.question.toLowerCase())
    );
    if (match) return match.answer;

    // Irrelevant input response
    return "❌ Sorry! I can only answer questions related to the school, like admissions, courses, contact details, or campus life.";
  };

  const handleSend = (customText) => {
    const message = customText || inputValue;
    if (!message.trim()) return;

    const userMsg = {
      text: message,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsTyping(true);

    setTimeout(() => {
      const response = getBotResponse(message);
      const botMsg = {
        text: response,
        sender: "bot",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 1000);

    setInputValue("");

    // Delay focusing only on desktop
    if (window.innerWidth >= 640) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      inputRef.current?.blur(); // mobile: don't open keyboard again
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Prevent body scroll when chatbot is open
  useEffect(() => {
    if (isActive) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isActive]);

  return (
    <div
      className={`fixed bottom-4 sm:bottom-8 right-2 sm:right-4 md:right-6 z-50 w-[calc(100vw-1rem)] sm:w-80 md:w-96 ${
        isActive ? "block" : "hidden"
      }`}
    >
      <div className="bg-white rounded-xl shadow-xl overflow-hidden flex flex-col h-[32rem] sm:h-[36rem] border border-gray-200">
        {/* Header */}
        <div className="bg-cyan-600 text-white py-3 px-4 font-semibold relative flex items-center">
          <div className="flex items-center">
            <div className="relative">
              <img
                src="https://res.cloudinary.com/ddvpczzng/image/upload/v1757446085/shasha_logo_tilt_yzbte7.png"
                alt="Shasha Bot"
                className="w-10 h-10 rounded-full border-2 border-white shadow-md"
                loading="lazy"
              />
              {isActive && (
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
              )}
            </div>
            <span className="text-lg md:text-xl ml-3">Shasha</span>
          </div>
          <button
            onClick={toggle}
            className="ml-auto text-white hover:text-gray-200 transition-colors"
            aria-label="Close chat"
          >
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </div>

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-3 relative backdrop-blur-sm"
          style={{
            backgroundImage: `url(https://res.cloudinary.com/ddvpczzng/image/upload/v1765084856/bg_dhddqj.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="relative z-10">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                } mb-3`}
              >
                <div
                  className={`px-4 py-2 rounded-lg text-sm sm:text-base max-w-[85%] sm:max-w-[75%] shadow-md whitespace-pre-wrap ${
                    msg.sender === "user"
                      ? "bg-cyan-500 text-white rounded-tr-none"
                      : "bg-white bg-opacity-90 text-gray-800 rounded-tl-none"
                  }`}
                >
                  {typeof msg.text === "string" ? msg.text : msg.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white bg-opacity-90 rounded-lg rounded-tl-none px-4 py-2 max-w-[85%] sm:max-w-[75%]">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce delay-200"></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Suggestions */}
        <div className="flex flex-wrap gap-1.5 p-2 border-t border-gray-200 bg-white">
          {suggestions.map((text, idx) => (
            <button
              key={idx}
              onClick={() => handleSend(text)}
              className="text-xs px-2.5 py-1  bg-cyan-500 text-white hover:bg-cyan-600 transition-colors flex-1 min-w-[30%] text-center"
            >
              {text}
            </button>
          ))}
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 p-2 bg-white rounded-b-xl">
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Ask something..."
              className="flex-1 p-2 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-1 focus:ring-cyan-300"
            />
            <button
              onClick={() => handleSend()}
              className="bg-cyan-600 hover:bg-cyan-700 text-white p-2 rounded-full transition-colors min-w-[40px]"
              aria-label="Send message"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="text-sm" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
